import { useEffect, useId, useMemo } from 'react'
import { initialQueryState, IconPaths, ModalSize, useDebounce } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  ActiveFilter,
  ActiveFiltersBar,
  Button,
  CustomSelect,
  IOption,
  Input,
  TableFilter,
  TableFilters,
  useModal,
} from 'app/components'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import { opcoesTipoFornecedor } from '../../../../dispositivos/core/rotulos'
import { TipoFornecedor } from '../../../core/_models'
import { FornecedorModal } from '../../../components/modal/FornecedorModal'
import { buscaFornecedores } from '../../../core/buscaFornecedores'

export const FornecedoresListHeader = () => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { showModal } = useModal()
  const { t } = useT()
  const idNome = useId()
  const idTipo = useId()
  const idAtivo = useId()
  const total = response?.totalElements ?? 0
  const debounced = useDebounce(filters, 500)

  const rotuloAtivos = t('pagina.fornecedores.filtro.ativos')
  const rotuloInativos = t('pagina.fornecedores.filtro.inativos')
  // Referência estável: o CustomSelect ressincroniza `options` a cada identidade nova.
  const opcoesTipos = useMemo(() => opcoesTipoFornecedor(t), [t])
  const opcoesAtivo: IOption<boolean>[] = useMemo(
    () => [
      { value: true, label: rotuloAtivos },
      { value: false, label: rotuloInativos },
    ],
    [rotuloAtivos, rotuloInativos]
  )

  const search = useMemo(
    () =>
      buscaFornecedores({
        texto: debounced.nome,
        ativo: debounced.ativo?.value,
        tipos: debounced.tipo ? [debounced.tipo.value] : undefined,
      }),
    [debounced]
  )

  useEffect(() => {
    updateState({ ...initialQueryState, search })
  }, [search, updateState])

  const activeFilters: ActiveFilter[] = [
    filters.nome && {
      key: 'nome',
      label: t('pagina.fornecedores.filtro.nome'),
      value: filters.nome,
      onRemove: () => setFilter('nome', ''),
    },
    filters.tipo && {
      key: 'tipo',
      label: t('pagina.fornecedores.filtro.tipo'),
      value: filters.tipo.label,
      onRemove: () => setFilter('tipo', undefined),
    },
    filters.ativo && {
      key: 'ativo',
      label: t('situacao.label'),
      value: filters.ativo.label,
      onRemove: () => setFilter('ativo', undefined),
    },
  ].filter((filtro): filtro is ActiveFilter => Boolean(filtro))

  const abrirNovo = () =>
    showModal({
      title: t('pagina.fornecedores.modal.titulo_novo'),
      content: <FornecedorModal />,
      size: ModalSize.Large,
    })

  return (
    <div className='card-header border-0 pt-6'>
      <TableFilters
        actions={
          <Button className='btn-primary' icon={IconPaths.Add} onClick={abrirNovo}>
            {t('pagina.fornecedores.novo')}
          </Button>
        }
        footer={
          <ActiveFiltersBar
            filters={activeFilters}
            onClearAll={clearFilters}
            summary={
              isLoading
                ? t('pagina.dispositivos.buscando')
                : t('pagina.fornecedores.total', { total })
            }
          />
        }
      >
        <TableFilter label={t('pagina.fornecedores.filtro.nome')} htmlFor={idNome} wide>
          <Input
            id={idNome}
            value={filters.nome}
            onChange={(e) => setFilter('nome', e.target.value)}
          />
        </TableFilter>
        <TableFilter label={t('pagina.fornecedores.filtro.tipo')} htmlFor={idTipo}>
          <CustomSelect
            inputId={idTipo}
            onChange={(tipo?: IOption<TipoFornecedor>) => setFilter('tipo', tipo ?? undefined)}
            options={opcoesTipos}
            value={filters.tipo}
            isClearable
          />
        </TableFilter>
        <TableFilter label={t('situacao.label')} htmlFor={idAtivo}>
          <CustomSelect
            inputId={idAtivo}
            onChange={(ativo?: IOption<boolean>) => setFilter('ativo', ativo ?? undefined)}
            options={opcoesAtivo}
            value={filters.ativo}
            isClearable
          />
        </TableFilter>
      </TableFilters>
    </div>
  )
}
